// Breadcrumb-navigated destination picker. Takes a listDir function
// rather than importing the hook/worker directly so it is easily
// testable. The parent passes a bound `(h, path) => worker.listDir(h, path)`.

import { useEffect, useMemo, useState } from "react";
import { BottomSheet } from "../ui/bottom-sheet";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, FolderPlus } from "lucide-react";
import type { DirListing } from "../../worker/api";
import { NewFolderSheet } from "./NewFolderSheet";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  handle: number;
  sourceParent: string;
  sourceNames: string[];
  count: number;
  listDir: (handle: number, path: string) => Promise<DirListing>;
  onCreateFolder: (parentPath: string, name: string) => Promise<void>;
  onMoveHere: (destPath: string) => void;
}

function join(parent: string, name: string): string {
  if (parent === "/" || parent === "") return `/${name}`;
  return `${parent}/${name}`;
}

function pathParent(path: string): string {
  if (path === "/" || path === "") return "/";
  const i = path.lastIndexOf("/");
  return i <= 0 ? "/" : path.slice(0, i);
}

export function MoveToPicker({
  open,
  onOpenChange,
  handle,
  sourceParent,
  sourceNames,
  count,
  listDir,
  onCreateFolder,
  onMoveHere,
}: Props) {
  const [path, setPath] = useState("/");
  const [children, setChildren] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    listDir(handle, path)
      .then((listing) => {
        if (cancelled) return;
        setChildren(
          listing.entries
            .filter((e) => e.kind === "dir" && !e.name.startsWith("."))
            .map((e) => e.name),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, handle, path, listDir]);

  const sourcePaths = useMemo(
    () => new Set(sourceNames.map((n) => join(sourceParent, n))),
    [sourceNames, sourceParent],
  );
  const isDisabled = (childPath: string) => {
    for (const src of sourcePaths) {
      if (childPath === src || childPath.startsWith(`${src}/`)) return true;
    }
    return false;
  };

  const moveHereDisabled = path === sourceParent;

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={`Move ${count} item${count === 1 ? "" : "s"} to…`}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            disabled={moveHereDisabled}
            onClick={() => onMoveHere(path)}
          >
            Move here
          </Button>
        </>
      }
    >
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          aria-label="Back"
          disabled={path === "/"}
          onClick={() => setPath(pathParent(path))}
          className="grid h-7 w-7 place-items-center rounded border border-border text-text-soft disabled:opacity-40"
        >
          <ChevronLeft size={14} />
        </button>
        <span className="truncate text-sm text-text-soft">
          <span className="text-text">{path === "/" ? "Vault" : path}</span>
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        {loading && (
          <div className="p-4 text-center text-xs text-text-dim">Loading…</div>
        )}
        {!loading && children.length === 0 && (
          <div className="p-4 text-center text-xs text-text-dim">
            No subfolders here.
          </div>
        )}
        {!loading &&
          children.map((name) => {
            const childPath = join(path, name);
            const disabled = isDisabled(childPath);
            return (
              <button
                key={name}
                type="button"
                disabled={disabled}
                onClick={() => setPath(childPath)}
                className="flex w-full items-center gap-3 border-b border-border-soft px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-bg-sidebar disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <div className="h-4 w-5 rounded bg-accent-soft/60" />
                <span className="flex-1 truncate">{name}</span>
                {!disabled && (
                  <ChevronRight size={14} className="text-text-dim" />
                )}
              </button>
            );
          })}
      </div>

      <button
        type="button"
        onClick={() => setCreating(true)}
        className="mt-3 flex w-full items-center gap-2 rounded-lg border border-dashed border-border bg-bg-raised px-3 py-2.5 text-sm font-medium text-accent"
      >
        <FolderPlus size={16} /> New folder here
      </button>

      {creating && (
        <NewFolderSheet
          mode="create"
          destPath={path}
          siblings={children}
          initialName=""
          open
          onOpenChange={setCreating}
          onSubmit={async (name) => {
            await onCreateFolder(path, name);
            setPath(join(path, name));
          }}
        />
      )}
    </BottomSheet>
  );
}
